import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { TokenStorage } from '../services/api';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com/api';

function buildDownloadTitle(title: string) {
  const safeTitle = (title || '').replace(/[\\/:*?"<>|]+/g, ' ').trim();
  return `${safeTitle || 'Report'}-mannaplag`;
}

function useDownloadFilename(title: string) {
  useLayoutEffect(() => {
    const prevTitle = document.title;
    document.title = buildDownloadTitle(title);
    return () => { document.title = prevTitle; };
  }, [title]);
}


async function apiFetch(endpoint: string) {
  const token = TokenStorage.getAccess();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

interface Match {
  id: string;
  paper_title: string;
  paper_url: string;
  match_type: string;
  similarity_pct: number;
  matched_chunks: number;
  sample_text: string;
}

interface HighlightedChunk {
  chunk_idx: number;
  text: string;
  is_reference: boolean;
  source_idx: number | null;
  match_type: string | null;
}

interface AISentence {
  text: string;
  ai_probability: number;
  is_flagged: boolean;
}

interface AISection {
  name: string;
  ai_score: number;
  word_count: number;
  flagged_sentences: { text: string; ai_probability: number }[];
  all_sentences?: AISentence[];
}

interface Report {
  id: string;
  report_type: 'plagiarism' | 'ai';
  overall_similarity?: number | string;
  verbatim_score?: number;
  mosaic_score?: number;
  total_chunks?: number;
  flagged_chunks?: number;
  generated_at?: string;
  matches?: Match[];
  highlighted_text?: HighlightedChunk[];
  submission?: {
    id: string;
    title: string;
    file_name: string;
    word_count: number;
    submitted_at: string;
  };
  overall_ai_score?: number;
  
  word_count?: number;
  file_name?: string;
  title?: string;
  sections?: AISection[];
  submitted_at?: string;
}

const SOURCE_COLORS = [
  '#e74c3c', '#e67e22', '#2ecc71', '#3498db',
  '#9b59b6', '#1abc9c', '#e91e63', '#ff5722',
  '#607d8b', '#f39c12', '#8e44ad', '#16a085',
  '#c0392b', '#d35400', '#27ae60', '#2980b9',
  '#7d3c98', '#148f77', '#cb4335', '#dc7633',
];

const SOURCE_BG = [
  'rgba(231,76,60,0.13)', 'rgba(230,126,34,0.13)', 'rgba(46,204,113,0.13)', 'rgba(52,152,219,0.13)',
  'rgba(155,89,182,0.13)', 'rgba(26,188,156,0.13)', 'rgba(233,30,99,0.13)', 'rgba(255,87,34,0.13)',
  'rgba(96,125,139,0.13)', 'rgba(243,156,18,0.13)', 'rgba(142,68,173,0.13)', 'rgba(22,160,133,0.13)',
  'rgba(192,57,43,0.13)', 'rgba(211,84,0,0.13)', 'rgba(39,174,96,0.13)', 'rgba(41,128,185,0.13)',
  'rgba(125,60,152,0.13)', 'rgba(20,143,119,0.13)', 'rgba(203,67,53,0.13)', 'rgba(220,118,51,0.13)',
];

// AI highlight color — Turnitin uses a teal/cyan
const AI_COLOR = '#0ea5e9';
const AI_BG = 'rgba(14,165,233,0.12)';

const scoreColor = (score: number) => {
  if (score > 40) return '#e74c3c';
  if (score > 20) return '#f39c12';
  if (score > 10) return '#f1c40f';
  return '#27ae60';
};

const aiScoreColor = (score: number) => {
  if (score > 60) return '#e74c3c';
  if (score > 30) return '#f39c12';
  if (score > 10) return '#0ea5e9';
  return '#27ae60';
};

const scoreLabel = (score: number, type: 'similarity' | 'ai') => {
  if (type === 'ai') {
    if (score > 60) return 'High AI Content';
    if (score > 30) return 'Moderate AI Content';
    if (score > 10) return 'Low AI Content';
    return 'Very Low AI Content';
  }
  if (score > 40) return 'High Similarity';
  if (score > 20) return 'Moderate Similarity';
  if (score > 10) return 'Low Similarity';
  return 'Very Low Similarity';
};

const SourceBadge = ({ num, color }: { num: number; color: string }) => {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 18, height: 18, borderRadius: 4,
      backgroundColor: color, color: '#fff',
      fontSize: 10, fontWeight: 800,
      marginRight: 3, verticalAlign: 'middle', lineHeight: 1,
    }}>
      {num}
    </span>
  );
}

// ── Plagiarism Report — Professional layout matching Turnitin standard ──
function PlagiarismReport({ report }: { report: Report }) {
  const navigate = useNavigate();
  // Every source the backend returns has passed its qualification rules
  // (report_generator._build_source_matches); small ones render as "<1%".
  const matches = (report.matches ?? []).filter(m => m.similarity_pct > 0);
  const highlighted = report.highlighted_text ?? [];
  const score = report.overall_similarity ?? 0;
  const color = scoreColor(score);
  const label = scoreLabel(score, 'similarity');
  const sub = report.submission!;
  const submittedAt = sub?.submitted_at
    ? new Date(sub.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : report.generated_at
      ? new Date(report.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
      : '';

  // const internetMatches = matches.filter(m => m.paper_url);
  // const publicationMatches = matches.filter(m => !m.paper_url);
  // const internetPct = internetMatches.reduce((a, m) => a + m.similarity_pct, 0);
  // const publicationPct = publicationMatches.reduce((a, m) => a + m.similarity_pct, 0);

  const verbatim = report.verbatim_score ?? null;
  const mosaic = report.mosaic_score ?? null;
  const totalChunks = report.total_chunks ?? matches.length;
  const flaggedChunks = report.flagged_chunks ?? matches.length;

  return (
    <div className="min-h-screen print:bg-white" style={{ background: '#f8f9fb' }}>

      {/* Topbar */}
      <div className="print:hidden bg-white border-b border-gray-100 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-[13px] font-bold transition-colors">
          ← Back
        </button>
        <button onClick={() => { document.title = buildDownloadTitle(sub.title || sub.file_name || ''); window.print(); }}
          className="flex items-center gap-2 px-5 py-2.5 text-white text-[12px] font-black rounded-md transition-colors"
          style={{ background: '#003c6c' }}>
          Download PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto py-8 px-4 print:py-0 print:px-0 print:max-w-none">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden print:rounded-none print:shadow-none print:border-0">

          {/* ── Header: Logo + Score ── */}
          <div className="flex flex-col items-center py-10 border-b border-gray-100 print:py-8"
            style={{ background: 'linear-gradient(160deg, #f0f4ff 0%, #fff 60%)' }}>
            <img src="/manna-plag-logo-horizontal.png?v=2" alt="Manna Plag" className="h-14 mb-3 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.25em] mb-7">Similarity Report</p>
            <p className="text-[72px] font-black leading-none mb-1" style={{ color, letterSpacing: '-2px' }}>{score}%</p>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">overall similarity</p>
            <span style={{
              display: 'inline-block', padding: '4px 14px', borderRadius: 999,
              background: color + '15', color, fontSize: 12, fontWeight: 800,
              letterSpacing: '0.05em', marginTop: 4,
            }}>{label}</span>
          </div>

          {/* ── Paper metadata ── */}
          <div style={{ padding: '20px 32px', borderBottom: '1px solid #f3f4f6' }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 12 }}>
              {sub?.title || sub?.file_name}
            </p>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              {submittedAt && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Submitted</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{submittedAt}</p>
                </div>
              )}
              {(sub?.word_count ?? 0) > 0 && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Words</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{sub.word_count.toLocaleString()}</p>
                </div>
              )}
              {flaggedChunks > 0 && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Matched Segments</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{flaggedChunks} of {totalChunks}</p>
                </div>
              )}
              {matches.length > 0 && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Sources Found</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{matches.length}</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Match breakdown + Top source types ── */}
          <div style={{ padding: '20px 32px', borderBottom: '1px solid #f3f4f6', background: '#fafbff', display: 'flex', gap: '3rem', flexWrap: 'wrap' }}>

            {/* Match type breakdown */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>Match Breakdown</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {verbatim !== null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#e74c3c', display: 'inline-block' }} />
                      <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Verbatim match</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#e74c3c' }}>{verbatim}%</span>
                  </div>
                )}
                {mosaic !== null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#f39c12', display: 'inline-block' }} />
                      <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Mosaic / paraphrase</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#f39c12' }}>{mosaic}%</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: '#6b7280', display: 'inline-block' }} />
                    <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Bibliography excluded</span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>Yes</span>
                </div>
              </div>
            </div>

            {/* Top source types */}
            <div style={{ flex: 1, minWidth: 180 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>Sources Found</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {matches.length > 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>📚 Academic Database</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: scoreColor(report?.overall_similarity) }}>{matches.length} source{matches.length !== 1 ? 's' : ''}</span>
                  </div>
                ) : (
                  <p style={{ fontSize: 12, color: '#9ca3af' }}>No sources found</p>
                )}
              </div>
            </div>
          </div>

          {/* ── Highlighted manuscript text ── */}
          {highlighted.length > 0 && (
            <div style={{ padding: '20px 32px', borderBottom: '1px solid #f3f4f6' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>
                Manuscript Text
              </p>
              <div style={{ fontSize: 13, lineHeight: 2, color: '#1f2937' }}>
                {highlighted.map((chunk) => {
                  if (chunk.is_reference) {
                    return <span key={chunk.chunk_idx} style={{ color: '#d1d5db' }}>{chunk.text} </span>;
                  }
                  if (chunk.source_idx != null && chunk.source_idx > 0) {
                    const ci = (chunk.source_idx - 1) % SOURCE_COLORS.length;
                    return (
                      <span key={chunk.chunk_idx}>
                        <span style={{ backgroundColor: SOURCE_BG[ci], borderBottom: `2px solid ${SOURCE_COLORS[ci]}`, padding: '1px 0' }}>
                          <SourceBadge num={chunk.source_idx} color={SOURCE_COLORS[ci]} />
                          <span style={{ color: SOURCE_COLORS[ci] }}>{chunk.text}</span>
                        </span>{' '}
                      </span>
                    );
                  }
                  return <span key={chunk.chunk_idx}>{chunk.text} </span>;
                })}
              </div>
            </div>
          )}

          {/* ── Sources list ── */}
          <div style={{ padding: '20px 32px', borderBottom: '1px solid #f3f4f6' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>
              Top Sources
            </p>
            <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 16 }}>
              Sources with the highest number of matches. Overlapping sources may not be shown separately.
            </p>
            {matches.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#374151', margin: 0 }}>No significant matches found</p>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>This manuscript has very low similarity to known sources.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {matches.map((m, i) => {
                  const ci = i % SOURCE_COLORS.length;
                  // Extract DOI from title if no paper_url (e.g. Turkish journal)
                  const titleDoi = !m.paper_url && m.paper_title
                    ? (() => { const match = m.paper_title.match(/10\.\d{4,}\/[\S]+/); return match ? 'https://doi.org/' + match[0].replace(/[.,;)]+$/, '') : null; })()
                    : null;

                  const url = m.paper_url || titleDoi || null;
                  const isInternet = !!url;

                  // Show paper title as primary display name
                  const displayName = m.paper_title && m.paper_title.trim()
                    ? m.paper_title.trim()
                    : (url
                      ? (() => { try { return new URL(url).hostname.replace('www.', ''); } catch { return url; } })()
                      : 'Unknown source');

                  // Build clean URL label and link
                  const urlLabel = url
                    ? (() => {
                      if (url.includes('doi.org/10.48550/arXiv') || url.includes('arxiv.org/abs')) return 'arXiv: ' + url.split('/').pop();
                      if (url.includes('doi.org/')) return 'DOI: ' + url.replace('https://doi.org/', '').replace('http://doi.org/', '');
                      if (url.includes('eric.ed.gov')) return 'ERIC: ' + (url.split('id=')[1] || url);
                      if (url.includes('ncbi.nlm.nih.gov/pmc')) return 'PubMed Central: ' + (url.split('/articles/')[1] || '').replace('/', '');
                      if (url.includes('europepmc.org')) return 'Europe PMC';
                      if (url.includes('zenodo.org')) return 'Zenodo: ' + url.split('/').pop();
                      if (url.includes('openalex.org')) return 'OpenAlex';
                      try { return new URL(url).hostname.replace('www.', ''); } catch { return url; }
                    })()
                    : null;
                  return (
                    <div key={m.id || i} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 0', borderBottom: i < matches.length - 1 ? '1px solid #f3f4f6' : 'none',
                    }}>
                      {/* Number badge */}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        backgroundColor: SOURCE_COLORS[ci], color: '#fff',
                        fontSize: 12, fontWeight: 800,
                      }}>{i + 1}</span>

                      {/* Source info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {url ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{
                            fontSize: 13, fontWeight: 700, color: '#111827',
                            textDecoration: 'none', display: 'block',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                            onMouseOver={e => (e.currentTarget.style.color = '#1d4ed8')}
                            onMouseOut={e => (e.currentTarget.style.color = '#111827')}>
                            {displayName}
                          </a>
                        ) : (
                          <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {displayName}
                          </p>
                        )}
                        {url && urlLabel && (
                          <a href={url} target="_blank" rel="noopener noreferrer" style={{
                            fontSize: 10, color: '#6b7280', margin: '2px 0 0', display: 'block',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            textDecoration: 'none',
                          }}
                            onMouseOver={e => (e.currentTarget.style.color = '#1d4ed8')}
                            onMouseOut={e => (e.currentTarget.style.color = '#6b7280')}>
                            {urlLabel}
                          </a>
                        )}
                      </div>

                      {/* Type pill */}
                      <span style={{
                        fontSize: 10, fontWeight: 700, flexShrink: 0,
                        padding: '3px 10px', borderRadius: 999,
                        background: '#f3f4f6',
                        color: '#6b7280',
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                      }}>

                      </span>

                      {/* Score */}
                      <span style={{
                        fontSize: 15, fontWeight: 900, flexShrink: 0,
                        width: 44, textAlign: 'right',
                        color: SOURCE_COLORS[ci],
                      }}>
                        {m.similarity_pct < 1 ? '<1%' : `${m.similarity_pct}%`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Disclaimer ── */}
          <div style={{ padding: '14px 32px', background: '#fffbeb', borderTop: '1px solid #fef3c7' }}>
            <p style={{ fontSize: 11, color: '#78716c', lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: '#92400e' }}>Note:</strong>{' '}
              Similarity scores indicate textual overlap with known sources and do not by themselves constitute evidence of plagiarism.
              Bibliography, quoted text, and small matches under 10 words are excluded from this report.
              Human judgment is required to determine whether any academic misconduct has occurred.
            </p>
          </div>

          {/* ── Footer ── */}
          <div style={{ padding: '14px 32px', borderTop: '1px solid #f3f4f6', background: '#f9fafb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', margin: 0 }}>Generated by Manna Plag</p>
                <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
                  Similarity Detection · {report.generated_at ? new Date(report.generated_at).toLocaleString('en-IN') : ''}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>Manna Analytics Private Limited</p>
                <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>Nagercoil, Tamil Nadu, India</p>
              </div>
            </div>
          </div>

        </div>
      </div>
      <style>{`@media print { @page { margin: 0.5in; size: A4; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .print\\:hidden { display: none !important; } .print\\:bg-white { background: white !important; } .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; } .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; } .print\\:max-w-none { max-width: none !important; } .print\\:rounded-none { border-radius: 0 !important; } .print\\:shadow-none { box-shadow: none !important; } .print\\:border-0 { border: none !important; } }`}</style>
    </div>
  );
}



// ── AI Report — Professional layout with section breakdown ───
function AIReport({ report }: { report: Report }) {
  const navigate = useNavigate();
  const aiScore = report.overall_ai_score ?? 0;
  const color = aiScoreColor(aiScore);
  const label = scoreLabel(aiScore, 'ai');
  const sections = report.sections ?? [];
  const fileName = report.file_name ?? '';
  const title = report.title || fileName;
  useDownloadFilename(title);
  const submittedAt = report.submitted_at
    ? new Date(report.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const hasAllSentences = sections.some(s => s.all_sentences && s.all_sentences.length > 0);

  // Segment count — total flagged sentences across all sections
  const totalFlagged = sections.reduce((acc, sec) => {
    if (sec.all_sentences) return acc + sec.all_sentences.filter(s => s.is_flagged).length;
    return acc + sec.flagged_sentences.length;
  }, 0);
  const totalSentences = sections.reduce((acc, sec) => {
    return acc + (sec.all_sentences?.length ?? sec.flagged_sentences.length);
  }, 0);

  // Score bar color
  function sectionBarColor(score: number) {
    if (score >= 60) return '#e74c3c';
    if (score >= 30) return '#f39c12';
    if (score >= 10) return '#0ea5e9';
    return '#22c55e';
  }

  return (
    <div className="min-h-screen print:bg-white" style={{ background: '#f8f9fb' }}>

      {/* Topbar */}
      <div className="print:hidden bg-white border-b border-gray-100 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-[13px] font-bold transition-colors">
          ← Back
        </button>
        <button onClick={() => { document.title = buildDownloadTitle(title); window.print(); }}
          className="flex items-center gap-2 px-5 py-2.5 text-white text-[12px] font-black rounded-md transition-colors"
          style={{ background: '#003c6c' }}>
          Download PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto py-8 px-4 print:py-0 print:px-0 print:max-w-none">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden print:rounded-none print:shadow-none print:border-0">

          {/* ── Header: Logo + Score ── */}
          <div className="flex flex-col items-center py-10 border-b border-gray-100 print:py-8"
            style={{ background: 'linear-gradient(160deg, #f0f4ff 0%, #fff 60%)' }}>
            <img src="/manna-plag-logo-horizontal.png?v=2" alt="Manna Plag" className="h-14 mb-3 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-[0.25em] mb-7">AI Detection Report</p>
            <p className="text-[72px] font-black leading-none mb-1" style={{ color, letterSpacing: '-2px' }}>{aiScore}%</p>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">detected as AI</p>
            <span style={{
              display: 'inline-block',
              padding: '4px 14px',
              borderRadius: 999,
              background: color + '15',
              color,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.05em',
              marginTop: 4,
            }}>{label}</span>
          </div>

          {/* ── Paper metadata ── */}
          <div className="px-8 py-5 border-b border-gray-100">
            <p className="text-[15px] font-bold text-gray-900 leading-snug mb-3">{title}</p>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              {submittedAt && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Submitted</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{submittedAt}</p>
                </div>
              )}
              {(report.word_count ?? 0) > 0 && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Word Count</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{report.word_count!.toLocaleString()}</p>
                </div>
              )}
              {totalSentences > 0 && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>AI Segments</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{totalFlagged} of {totalSentences}</p>
                </div>
              )}
              {fileName && (
                <div>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>File</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{fileName}</p>
                </div>
              )}
            </div>
          </div>

          {/* ── AI Summary row ── */}
          <div className="px-8 py-4 border-b border-gray-100" style={{ background: '#fafbff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{
                width: 12, height: 12, borderRadius: 3,
                backgroundColor: AI_COLOR, flexShrink: 0, display: 'inline-block',
              }} />
              <span style={{ fontSize: 12, fontWeight: 800, color: '#374151' }}>
                {totalFlagged} AI-generated segment{totalFlagged !== 1 ? 's' : ''} detected
              </span>
              <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>
                Likely generated by a large language model
              </span>
            </div>
          </div>

          {/* ── Section breakdown ── */}
          {sections.length > 0 && (
            <div className="px-8 py-6 border-b border-gray-100">
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>
                Section Breakdown
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sections.map((sec, i) => {
                  const barColor = sectionBarColor(sec.ai_score);
                  const secFlagged = sec.all_sentences
                    ? sec.all_sentences.filter(s => s.is_flagged).length
                    : sec.flagged_sentences.length;
                  const secTotal = sec.all_sentences?.length ?? sec.flagged_sentences.length;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {/* Section name */}
                      <div style={{ width: 160, flexShrink: 0 }}>
                        <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {sec.name}
                        </p>
                        <p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>
                          {sec.word_count > 0 ? `${sec.word_count.toLocaleString()} words` : ''}{secTotal > 0 ? ` · ${secFlagged}/${secTotal} segments` : ''}
                        </p>
                      </div>
                      {/* Bar */}
                      <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(sec.ai_score, 100)}%`,
                          background: barColor,
                          borderRadius: 999,
                          transition: 'width 0.6s ease',
                        }} />
                      </div>
                      {/* Score */}
                      <div style={{ width: 44, textAlign: 'right', flexShrink: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 900, color: barColor }}>{sec.ai_score}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Inline highlighted full text ── */}
          {hasAllSentences && (
            <div className="px-8 py-6 border-b border-gray-100">
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>
                Manuscript Text
              </p>
              {sections.map((sec, sIdx) => (
                <div key={sIdx} style={{ marginBottom: sIdx < sections.length - 1 ? 24 : 0 }}>
                  {/* Section header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                    paddingBottom: 8, borderBottom: '1px solid #f3f4f6',
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: 800, color: '#374151',
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                    }}>{sec.name}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      color: sectionBarColor(sec.ai_score),
                      background: sectionBarColor(sec.ai_score) + '15',
                      borderRadius: 999, padding: '2px 8px',
                    }}>{sec.ai_score}% AI</span>
                  </div>
                  {/* Sentences */}
                  <div style={{ fontSize: 13, lineHeight: 2, color: '#1f2937' }}>
                    {sec.all_sentences?.map((sentence, i) => {
                      if (sentence.is_flagged) {
                        return (
                          <span key={`${sIdx}-${i}`} style={{
                            backgroundColor: AI_BG,
                            borderBottom: `2px solid ${AI_COLOR}`,
                            color: '#0369a1',
                            padding: '1px 0',
                          }}>
                            {sentence.text}{' '}
                          </span>
                        );
                      }
                      return <span key={`${sIdx}-${i}`}>{sentence.text}{' '}</span>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Fallback for old reports without all_sentences */}
          {!hasAllSentences && sections.some(s => s.flagged_sentences.length > 0) && (
            <div className="px-8 py-6 border-b border-gray-100">
              <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>
                Flagged Sentences
              </p>
              {sections.filter(s => s.flagged_sentences.length > 0).map(sec => (
                <div key={sec.name} className="mb-4">
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8 }}>{sec.name}</p>
                  {sec.flagged_sentences.map((fs, i) => (
                    <div key={i} style={{ border: '1px solid #e0f2fe', borderRadius: 10, padding: '10px 14px', marginBottom: 8, background: '#f0f9ff' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: AI_COLOR, textTransform: 'uppercase', letterSpacing: '0.08em' }}>AI Probability</span>
                        <span style={{ fontSize: 13, fontWeight: 900, color: AI_COLOR }}>{fs.ai_probability}%</span>
                      </div>
                      <p style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, margin: 0 }}>
                        {fs.text.slice(0, 500)}{fs.text.length > 500 ? '...' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* ── Disclaimer ── */}
          <div style={{ padding: '14px 32px', borderTop: '1px solid #f3f4f6', background: '#fffbeb' }}>
            <p style={{ fontSize: 11, color: '#78716c', lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: '#92400e' }}>Caution: Review required.</strong>{' '}
              Our AI writing assessment is designed to help educators identify text that might be prepared by a generative AI tool.
              It may not always be accurate and should not be used as the sole basis for adverse actions against a student.
              Further scrutiny and human judgment are required to determine whether any academic misconduct has occurred.
            </p>
          </div>

          {/* ── Footer ── */}
          <div style={{ padding: '14px 32px', borderTop: '1px solid #f3f4f6', background: '#f9fafb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', margin: 0 }}>Generated by Manna Plag</p>
                <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>AI Detection · Conservative mode · ModernBERT classifier</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>Manna Analytics Private Limited</p>
                <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>Nagercoil, Tamil Nadu, India</p>
              </div>
            </div>
          </div>

        </div>
      </div>
      <style>{`@media print { @page { margin: 0.5in; size: A4; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .print\\:hidden { display: none !important; } .print\\:bg-white { background: white !important; } .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; } .print\\:px-0 { padding-left: 0 !important; padding-right: 0 !important; } .print\\:max-w-none { max-width: none !important; } .print\\:rounded-none { border-radius: 0 !important; } .print\\:shadow-none { box-shadow: none !important; } .print\\:border-0 { border: none !important; } }`}</style>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function ReportView() {
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type'); // 'plagiarism' | 'ai' | null (old links)
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        if (typeParam === 'ai') {
          const data = await apiFetch(`/checker/${reportId}/`);
          setReport({ ...data, report_type: 'ai' });
          return;
        }
        if (typeParam === 'plagiarism') {
          const data = await apiFetch(`/submissions/reports/${reportId}/`);
          setReport({ ...data, report_type: 'plagiarism' });
          return;
        }
        try {
          const data = await apiFetch(`/submissions/reports/${reportId}/`);
          setReport({ ...data, report_type: 'plagiarism' });
          return;
        } catch (e: any) {
          if (!e.message.includes('404')) throw e;
        }
        const data = await apiFetch(`/checker/${reportId}/`);
        setReport({ ...data, report_type: 'ai' });
      } catch {
        setError('Report not found.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [reportId, typeParam]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-[#003c6c] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-sm font-medium text-gray-600">
            Loading report...
          </p>
        </div>
      </div>
    );
  }

  if (error || !report) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <p className="text-[16px] font-black text-gray-800 mb-2">Report not found</p>
        <button onClick={() => navigate(-1)} className="text-[13px] text-blue-500 hover:underline">← Go back</button>
      </div>
    </div>
  );

  if (report.report_type === 'ai') return <AIReport report={report} />;
  return <PlagiarismReport report={report} />;
}